<template>
  <div id="app">
    <NavBar />
    <section
      class="content-wrapper"
      :class="{ 'without-navbar': !isAuthenticated }"
      v-if="isAuthenticated || isPageThatAllowsUnauthenticatedAccess()"
    >
      <router-view :key="currentWorkspace.id"></router-view>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { workspacesApi } from '@/workspaces/workspaces';
import { config } from '@/config/config';
import connectWidget from '@/connect/connectWidget';
import { useAuth } from '@/authentication/useAuth';
import NavBar from '@shared/components/navbar/NavBar.vue';

// Vuex store
const store = useStore();

// Router and Route
const router = useRouter();
const route = useRoute();
const { userId, isAuthenticated, logout } = useAuth();

// Computed properties (Vuex getters)
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const activeRoute = computed(() => String(route?.name || ''));

// Lifecycle hook
onMounted(async () => {
  await addConnectWidget();
  await setupRouteHooks();
});

function isPageThatAllowsUnauthenticatedAccess() {
  return routeStartsWith('register');
}

function routeStartsWith(prefix: string) {
  return activeRoute.value.startsWith(prefix);
}

async function getUserCredentials() {
  if (config().webAssistantWorkspaceId && isAuthenticated.value) {
    const token = await workspacesApi.createUserTokenForConnect();
    return { token, userId: userId.value };
  }
}

async function addConnectWidget() {
  const userCredentials = await getUserCredentials();
  if (userCredentials) {
    connectWidget.addWebAssistant(userCredentials.userId, userCredentials.token);
  }
}

async function setupRouteHooks() {
  router.afterEach(async () => {
    const userCredentials = await getUserCredentials();
    if (userCredentials) {
      await connectWidget.update(userCredentials.userId, userCredentials.token);
    }
  });
}
</script>

<style lang="scss">
#app {
  .navbar {
    width: 4.5rem;
    display: flex;
    flex-basis: 5rem;
    flex-shrink: 0;
    background-color: #fff;
    flex-direction: column;
    height: 100%;
    position: fixed;
    box-shadow: 3px 0 8px 0 rgba(0, 0, 0, 0.1);
    z-index: 1;
    top: 0;
    .logo-container {
      padding: 1rem 0 0.5rem 0;
    }

    .nav-item-container {
      flex: 1;
    }
    .user-account-container {
      .navbar-dropdown {
        display: flex;
        flex-direction: column;
        min-width: 200px; /* Adjust as needed */
        max-height: calc(100vh - 100px); /* Ensure it doesn't overflow viewport */
        padding: 0;

        /* Scrollable workspace container */
        .workspace-scroll-container {
          flex: 1;
          overflow-y: auto;
          max-height: calc(100vh - 200px); /* Adjust based on footer height */
          padding: 0.5rem 0;

          .navbar-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1rem;
            font-size: medium;
            color: $color-netcall-body-text;

            &:hover {
              color: #ffffff;
              background-color: $secondary;
            }
          }
        }

        /* Fixed footer */
        .dropdown-footer {
          flex-shrink: 0;
          border-top: 1px solid #dbdbdb; /* Optional: visual separation */
          background-color: #fff; /* Match dropdown background */

          .navbar-item {
            padding: 0.75rem 1rem;
            font-size: medium;
            color: $color-netcall-body-text;

            &:hover {
              color: #ffffff;
              background-color: $secondary;
            }
          }

          .navbar-divider {
            margin: 0;
          }
        }
      }
    }

    .navbar__logo {
      height: 64px;
      background-image: url(./assets/connect-logo.png);
      background-repeat: no-repeat;
      background-position: center;
    }

    ul {
      width: 100%;
    }

    li {
      cursor: pointer;
      flex: 1;
      display: flex;
      justify-content: center;

      a:not(.navbar-item) {
        font-size: x-large;
        color: #bdbdbd;
        transition: color 50ms;
        padding: 1rem 1.5rem;
      }

      &.is-active {
        a {
          color: $secondary;
        }
      }

      &:hover {
        a {
          color: $secondary;
          background: none;
        }
      }
      .navbar-item.is-hoverable:hover .navbar-dropdown.is-boxed {
        opacity: 1;
        pointer-events: auto;
        transform: translateY(0);
      }
    }
  }

  /* Ensure the dropdown stays in the bottom-left corner */
  .navbar .user-account-container .navbar-dropdown {
    left: 4.5rem;
    bottom: 0.5rem;
    top: auto;
  }

  .is-disabled {
    pointer-events: none;
    opacity: 0.4;
  }
}
.content-wrapper {
  padding-left: 4.5rem;

  &.without-navbar {
    padding-left: 0;
  }
}

.navbar-dropdown {
  max-height: calc(100vh - 200px);
  overflow: auto;
}

.connect-banner-active {
  #app {
    .navbar {
      top: 56px;
      height: calc(100% - 56px);
    }
  }
}
</style>
